import { Metric } from '@strength/domain';
import {
  getDefaultSetMetrics,
  HasWorkoutLayer,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { nextSortOrder, targetSortOrder } from '@strength/utils';
import {
  MdAltRoute,
  MdBrowserNotSupported,
  MdClose,
  MdContentCopy,
  MdFlag,
  MdLibraryAdd,
  MdNote,
  MdOutlineContentPasteGo,
  MdPlaylistAdd,
  MdRemove,
  MdSaveAs,
  MdStraighten,
  MdSwapHoriz,
} from '@teamworksdev/icons';
import { useAtom } from 'jotai';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../../../reporting/reporting';
import {
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '../../../components/context-menu/context-menu';
import { Input } from '../../../components/input/input';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import {
  getRepBenchmarkGridsForExercise,
  useBenchmarks,
} from '../../../hooks/useBenchmarks';
import { useMetrics } from '../../../hooks/useMetrics';
import { useToast } from '../../../hooks/useToast';
import {
  groupMetricsByCategory,
  searchMetrics,
} from '../../../utils/metric-categories';
import { WorkoutTemplateSelection } from '../../library/workout-templates/workout-template-utils';
import {
  collectMultiDayPasteCommandsByPosition,
  pasteCommands,
} from '../copy-paste/copy-paste';
import { hasDisabledItem } from '../copy-paste/group-data';
import {
  buildWorkoutClipboard,
  workoutClipboardAtom,
} from '../copy-paste/workout-clipboard';
import {
  MiscMutationFns,
  WorkoutLayerCommand,
  WorkoutLayerModelName,
  WorkoutLayerMutationFns,
} from '../mutation-fns/types';
import { commandsForBulkClear } from '../mutation-fns/workout-draft';
import {
  BlockGroupBy,
  BrandColors,
  InstructionEditData,
  SetGroupedData,
  WorkoutGroupedData,
  WorkoutWithCycleAndSession,
} from '../types';
import { ContextMenuItemIcon } from './context-menu-item-icon';
import {
  ContextMenuPattern,
  processDataForContextMenu,
  WithWorkoutId,
} from './data-for-context-menu';
import { SectionColorContextMenuSub } from './SectionColorContextMenuSub';
import { SupersetContextMenuItems } from './superset-menu-items';

export function getContextMenuContent(
  groupedData: WorkoutGroupedData[],
  getMetric: (id: string) => Metric,
  metrics: Metric[],
  workoutLayers: HasWorkoutLayer[],
  mutationFns: WorkoutLayerMutationFns,
  bulkRunCommands: (commands: WorkoutLayerCommand[]) => void,
  setInstructionEditData: (data: InstructionEditData | null) => void,
  setWorkoutTemplateData: (
    workoutId: string,
    sectionIds: WorkoutTemplateSelection,
  ) => void,
  setNewSchemeData: (sets: WithWorkoutId<SetGroupedData>[]) => void,
  t: I18nTranslator,
  modelName: WorkoutLayerModelName,
  brandColors: BrandColors,
  miscMutationFns: MiscMutationFns,
  defaultBenchmarkGridId: string,
  canCreateScheme: boolean,
  canCreateTemplate: boolean,
  workouts: WorkoutWithCycleAndSession[],
  athleteWorkoutLayers?: HasWorkoutLayer[],
  blockWorkoutLayers?: HasWorkoutLayer[],
  groupBy?: BlockGroupBy,
  hasBlockBeenPublished?: boolean,
  // omit to hide the multi-day "Save as template" action (flag off, or
  // surfaces like the athlete sheet that don't support it)
  setMultiWorkoutTemplateData?: (
    selection: string[] | { workoutId: string; sectionIds: string[] }[],
  ) => void,
): ReactNode {
  const disabledItemPresent = hasDisabledItem(groupedData);
  const pattern = processDataForContextMenu(groupedData);

  const commonContextMenuGroup = (
    <CommonContextMenuGroup
      hasBulkClear={canEnableBulkClear(pattern)}
      hasDisabledItem={disabledItemPresent}
      modelName={modelName}
      workoutLayers={workoutLayers}
      groupedData={groupedData}
      workouts={workouts}
      bulkRunCommands={bulkRunCommands}
      blockWorkoutLayers={blockWorkoutLayers}
      groupBy={groupBy}
    />
  );

  // Read-only (e.g. completed) workouts keep their planned draft layers, so
  // they can still be turned into templates. When the selection contains
  // disabled items, destructive actions are hidden, but the
  // "Save as template" action is still offered for the patterns that
  // support it.
  if (!pattern || disabledItemPresent) {
    const action = pattern
      ? getSaveAsTemplateActionForDisabledSelection(
          pattern,
          groupBy,
          setMultiWorkoutTemplateData,
        )
      : null;
    if (!action) {
      return commonContextMenuGroup;
    }
    return (
      <>
        <DisabledWithTooltip
          disabled={!canCreateTemplate}
          reason={t('permissionDisabled.create', { item: t('templates') })}
        >
          <SaveAsTemplateContextMenuItem
            disabled={!canCreateTemplate}
            onSelect={() => {
              if (action.kind === 'single') {
                setWorkoutTemplateData(action.workoutId, action.sectionIds);
              } else {
                action.onSelect();
              }
            }}
          />
        </DisabledWithTooltip>
        <ContextMenuSeparator />
        {commonContextMenuGroup}
      </>
    );
  }
  if (pattern.type === 'oneEntity') {
    switch (pattern.data.type) {
      case 'Workout':
        return (
          <>
            <ContextMenuItem
              onSelect={() => {
                mutationFns.onClearWorkout(pattern.workoutId);
              }}
            >
              <ContextMenuItemIcon IconPath={MdRemove} />
              {t('clearWorkout', { count: 1 })}
            </ContextMenuItem>
            <DisabledWithTooltip
              disabled={!canCreateTemplate}
              reason={t('permissionDisabled.create', { item: t('templates') })}
            >
              <SaveAsTemplateContextMenuItem
                disabled={!canCreateTemplate}
                onSelect={() => {
                  // select all sectionIds from the workout to use in the template
                  setWorkoutTemplateData(pattern.workoutId, 'all');
                }}
              />
            </DisabledWithTooltip>

            <ContextMenuSeparator />

            {commonContextMenuGroup}
          </>
        );
      case 'WorkoutName': {
        const workout = workouts.find((w) => w.workoutId === pattern.workoutId);
        const hasActual = !!workout?.actual?.length;
        const workoutIsPublished = !!workout && workout.status.type !== 'draft';
        const showDelete =
          hasBlockBeenPublished === false && (workout?.exists ?? false);
        const showCancel =
          hasBlockBeenPublished === true && workoutIsPublished && !hasActual;
        if (groupBy === 'all') {
          return (
            <>
              <DisabledWithTooltip
                disabled={!canCreateTemplate}
                reason={t('permissionDisabled.create', {
                  item: t('templates'),
                })}
              >
                <SaveAsTemplateContextMenuItem
                  disabled={!canCreateTemplate}
                  onSelect={() => {
                    setWorkoutTemplateData(pattern.workoutId, 'all');
                  }}
                />
              </DisabledWithTooltip>
              <ContextMenuItem
                onSelect={() => {
                  if (miscMutationFns?.onEditWorkoutName) {
                    miscMutationFns.onEditWorkoutName(pattern.workoutId, null);
                  }
                }}
              >
                <ContextMenuItemIcon IconPath={MdRemove} />
                {t('clearWorkoutName')}
              </ContextMenuItem>
              <ContextMenuSeparator />
              {showDelete && (
                <ContextMenuItem
                  onSelect={() => {
                    miscMutationFns?.onDeleteWorkout?.(pattern.workoutId);
                  }}
                >
                  <ContextMenuItemIcon IconPath={MdRemove} />
                  {t('deleteWorkout')}
                </ContextMenuItem>
              )}
              {showCancel && (
                <ContextMenuItem
                  onSelect={() => {
                    miscMutationFns?.onCancelWorkout?.(pattern.workoutId);
                  }}
                >
                  <ContextMenuItemIcon IconPath={MdClose} />
                  {t('cancelWorkoutAction')}
                </ContextMenuItem>
              )}
              {(showDelete || showCancel) && <ContextMenuSeparator />}
              {commonContextMenuGroup}
            </>
          );
        }
        return (
          <>
            {showDelete && (
              <ContextMenuItem
                onSelect={() => {
                  miscMutationFns?.onDeleteWorkout?.(pattern.workoutId);
                }}
              >
                <ContextMenuItemIcon IconPath={MdRemove} />
                {t('deleteWorkout')}
              </ContextMenuItem>
            )}
            {showCancel && (
              <ContextMenuItem
                onSelect={() => {
                  miscMutationFns?.onCancelWorkout?.(pattern.workoutId);
                }}
              >
                <ContextMenuItemIcon IconPath={MdClose} />
                {t('cancelWorkoutAction')}
              </ContextMenuItem>
            )}
            {(showDelete || showCancel) && <ContextMenuSeparator />}
            <ContextMenuItem
              onSelect={() => {
                if (miscMutationFns?.onEditWorkoutName) {
                  miscMutationFns.onEditWorkoutName(pattern.workoutId, null);
                }
              }}
            >
              <ContextMenuItemIcon IconPath={MdRemove} />
              {t('clearWorkoutName')}
            </ContextMenuItem>
            <ContextMenuSeparator />
            {commonContextMenuGroup}
          </>
        );
      }
      case 'Set': {
        const exercise = pattern.data.exercise;
        const set = pattern.data.set;
        const metricIds = pattern.data.metrics.map((m) => m.metricId);
        const matchingSetMetrics = set.metrics.filter((m) =>
          metricIds.includes(m.metricId),
        );
        const anyMissingSetMetrics =
          metricIds.length > matchingSetMetrics.length;

        const metricDefinitions = pattern.data.metrics.flatMap((m) => {
          const metric = getMetric(m.metricId);
          return metric ? [metric] : [];
        });
        const isCollectionEnabled = metricDefinitions.some(
          (m) => m.isCollectable || m.isCollectable === undefined,
        );
        const forCollectionOn =
          anyMissingSetMetrics ||
          matchingSetMetrics.some((m) => !m.forCollection);
        return (
          <>
            <AddSetContextMenuItem
              direction="above"
              onAddSet={() =>
                mutationFns.onAddSet(
                  pattern.workoutId,
                  exercise.workoutExerciseId,
                  {
                    sortOrder: targetSortOrder(
                      exercise.sets,
                      set.sortOrder,
                      'before',
                    ),
                  },
                  getDefaultSetMetrics(exercise.metrics),
                )
              }
            />
            <AddSetContextMenuItem
              direction="below"
              onAddSet={() =>
                mutationFns.onAddSet(
                  pattern.workoutId,
                  exercise.workoutExerciseId,
                  {
                    sortOrder: targetSortOrder(
                      exercise.sets,
                      set.sortOrder,
                      'after',
                    ),
                  },
                  getDefaultSetMetrics(exercise.metrics),
                )
              }
            />
            <ContextMenuItem
              onSelect={() => {
                const setsInGroup =
                  exercise.sets.filter(
                    (s) => s.workoutSetGroupId === set.workoutSetGroupId,
                  ) ?? [];

                mutationFns.onRemoveSets(
                  pattern.workoutId,
                  setsInGroup.map((s) => s.workoutSetId),
                );
              }}
            >
              <ContextMenuItemIcon IconPath={MdRemove} />
              {t('removeSet')}
            </ContextMenuItem>
            {isCollectionEnabled && (
              <ContextMenuItem
                onSelect={() => {
                  const metricsToEdit = metricIds
                    .map((metricId) => {
                      const metricDef = getMetric(metricId);
                      if (!metricDef || !metricDef.isCollectable) {
                        return null;
                      }
                      return {
                        metricId,
                        forCollection: forCollectionOn,
                      };
                    })
                    .filter((m): m is NonNullable<typeof m> => m !== null);
                  mutationFns.onEditSetMetrics(
                    pattern.workoutId,
                    set.workoutSetId,
                    metricsToEdit,
                  );
                }}
              >
                <ContextMenuItemIcon IconPath={MdFlag} />
                {forCollectionOn
                  ? t('markForCollection')
                  : t('clearForCollection')}
              </ContextMenuItem>
            )}

            <ContextMenuSeparator />

            {commonContextMenuGroup}
          </>
        );
      }
      case 'ExerciseMetric': {
        const exercise = pattern.data.exercise;
        const metric = pattern.data.metric;
        const isSetsMetric = metric.metricType === 'sets';
        return (
          <>
            <AddorChangeMetricContextMenuSub
              metrics={metrics}
              currentMetricIds={exercise.metrics.map((m) => m.metricId)}
              onMetricSelected={(m) => {
                trackEvent('Strength | Block Builder | Add Metric', {
                  libraryExerciseId: exercise.libraryExerciseId,
                  metricId: m.id,
                });
                mutationFns.onAddExerciseMetric(
                  pattern.workoutId,
                  exercise.workoutExerciseId,
                  {
                    sortOrder: targetSortOrder(
                      exercise.metrics,
                      metric.sortOrder,
                      'after',
                    ),
                    metricId: m.id,
                    metricName: m.name,
                    metricType: m.type,
                  },
                );
              }}
            />
            {!isSetsMetric && (
              <AddorChangeMetricContextMenuSub
                metrics={metrics}
                currentMetricIds={exercise.metrics.map((m) => m.metricId)}
                metricId={metric.metricId}
                onMetricSelected={(m) => {
                  trackEvent('Strength | Block Builder | Change Metric', {
                    libraryExerciseId: exercise.libraryExerciseId,
                    oldMetricId: metric.metricId,
                    newMetricId: m.id,
                  });

                  mutationFns.onSwapExerciseMetric(
                    pattern.workoutId,
                    exercise.workoutExerciseId,
                    metric.workoutExerciseMetricId,
                    {
                      metricId: m.id,
                      metricName: m.name,
                      metricType: m.type,
                      sortOrder: metric.sortOrder,
                    },
                  );
                }}
              />
            )}
            {!isSetsMetric && (
              <ContextMenuItem
                onSelect={() => {
                  trackEvent('Strength | Block Builder | Remove Metric', {
                    libraryExerciseId: exercise.libraryExerciseId,
                    metricId: metric.metricId,
                  });
                  mutationFns.onRemoveExerciseMetric(
                    pattern.workoutId,
                    metric.workoutExerciseMetricId,
                  );
                }}
              >
                <ContextMenuItemIcon IconPath={MdRemove} />
                {t('removeMetric')}
              </ContextMenuItem>
            )}
            {!isSetsMetric && metric.metricType === 'intensity' && (
              <BenchmarkMetricContextMenuSub
                onSelectBenchmarkGrid={(gridId) => () => {
                  mutationFns.onEditExerciseMetric(
                    pattern.workoutId,
                    metric.workoutExerciseMetricId,
                    { benchmarkGridId: gridId },
                  );
                }}
                selectedBenchmarkGridId={metric.benchmarkGridId}
                defaultBenchmarkGridId={defaultBenchmarkGridId}
                libraryExerciseId={exercise.libraryExerciseId}
              />
            )}
            <ContextMenuSeparator />
            {commonContextMenuGroup}
          </>
        );
      }
      case 'Exercise': {
        const { exercise, superset, alternateGroup } = pattern.data;

        const isBaseExercise =
          !alternateGroup ||
          alternateGroup.exercises.find(
            (e) => e.workoutExerciseId === exercise.workoutExerciseId,
          )?.sortOrder === 0;

        // If an athlete has created a group for this exercise, we want to disable the ability
        // to do so from the base layer as we don't know how to gracefully merge them
        const athleteHasGroup =
          athleteWorkoutLayers?.some((l) =>
            l.layer.alternateExerciseAdds.some(
              (e) => e.workoutExerciseId === exercise.workoutExerciseId,
            ),
          ) ?? false;

        return (
          <>
            {exercise.metrics.length !== 0 && (
              <AddSetContextMenuItem
                onAddSet={() =>
                  mutationFns.onAddSet(
                    pattern.workoutId,
                    exercise.workoutExerciseId,
                    { sortOrder: nextSortOrder(exercise.sets) },
                    getDefaultSetMetrics(exercise.metrics),
                  )
                }
              />
            )}
            <ContextMenuItem
              onSelect={() => {
                mutationFns.onEditExercise(
                  pattern.workoutId,
                  exercise.workoutExerciseId,
                  {
                    isEachSide: !exercise.isEachSide,
                  },
                );
              }}
            >
              <ContextMenuItemIcon IconPath={MdSwapHoriz} />
              {exercise.isEachSide ? t('clearPerSide') : t('setPerSide')}
            </ContextMenuItem>
            <AddorChangeMetricContextMenuSub
              metrics={metrics}
              currentMetricIds={exercise.metrics.map((m) => m.metricId)}
              onMetricSelected={(m) =>
                mutationFns.onAddExerciseMetric(
                  pattern.workoutId,
                  exercise.workoutExerciseId,
                  {
                    sortOrder: nextSortOrder(exercise.metrics),
                    metricId: m.id,
                    metricName: m.name,
                    metricType: m.type,
                  },
                )
              }
            />
            <AddInstructionContextMenuItem
              onHandleOpenEditInstruction={() =>
                setInstructionEditData({
                  workoutId: pattern.workoutId,
                  workoutSectionId: exercise.workoutSectionId,
                  workoutExerciseId: exercise.workoutExerciseId,
                  instruction: exercise.instruction,
                })
              }
            />
            {isBaseExercise && (
              <div
                title={
                  athleteHasGroup
                    ? t('athletesHaveAlternatesAssigned')
                    : undefined
                }
              >
                <ContextMenuItem
                  onSelect={() => {
                    if (alternateGroup) {
                      mutationFns.onRemoveAlternateGroup(
                        pattern.workoutId,
                        alternateGroup.workoutAlternateGroupId,
                      );
                    } else {
                      mutationFns.onAddAlternateGroup(
                        pattern.workoutId,
                        exercise.workoutSectionId,
                        exercise.workoutExerciseId,
                        { sortOrder: exercise.sortOrder },
                      );
                    }
                  }}
                  disabled={athleteHasGroup}
                >
                  <ContextMenuItemIcon
                    IconPath={MdAltRoute}
                    className="-scale-x-100 rotate-90"
                  />
                  {alternateGroup
                    ? t('removeAlternateExercises')
                    : t('addAlternateExercise')}
                </ContextMenuItem>
              </div>
            )}
            {isBaseExercise && (
              <SupersetContextMenuItems
                modelName={modelName}
                exerciseGroupedData={pattern.data}
                workoutLayers={workoutLayers}
                bulkRunCommands={bulkRunCommands}
                blockWorkoutLayers={blockWorkoutLayers}
              />
            )}
            <ContextMenuItem
              onSelect={() => {
                mutationFns.onRemoveExercise(
                  pattern.workoutId,
                  exercise.workoutExerciseId,
                  alternateGroup?.workoutAlternateGroupId,
                  superset?.workoutSupersetId,
                );
              }}
            >
              <ContextMenuItemIcon IconPath={MdBrowserNotSupported} />
              {t('removeExercise')}
            </ContextMenuItem>

            <ContextMenuSeparator />

            {commonContextMenuGroup}
          </>
        );
      }
      case 'Section': {
        const section = pattern.data.section;
        if (groupBy === 'all') {
          return (
            <>
              <AddInstructionContextMenuItem
                onHandleOpenEditInstruction={() =>
                  setInstructionEditData({
                    workoutId: pattern.workoutId,
                    workoutSectionId: section.workoutSectionId,
                    instruction: section.instruction,
                  })
                }
              />
              <ContextMenuItem
                onSelect={() =>
                  mutationFns.onRemoveSection(
                    pattern.workoutId,
                    section.workoutSectionId,
                  )
                }
              >
                <ContextMenuItemIcon IconPath={MdRemove} />
                {t('removeSection')}
              </ContextMenuItem>
              <ContextMenuSeparator />
              {commonContextMenuGroup}
            </>
          );
        }
        return (
          <>
            <DisabledWithTooltip
              disabled={!canCreateTemplate}
              reason={t('permissionDisabled.create', { item: t('templates') })}
            >
              <SaveAsTemplateContextMenuItem
                disabled={!canCreateTemplate}
                onSelect={() =>
                  setWorkoutTemplateData(pattern.workoutId, [
                    section.workoutSectionId,
                  ])
                }
              />
            </DisabledWithTooltip>
            <ContextMenuSeparator />

            <AddInstructionContextMenuItem
              onHandleOpenEditInstruction={() =>
                setInstructionEditData({
                  workoutId: pattern.workoutId,
                  workoutSectionId: section.workoutSectionId,
                  instruction: section.instruction,
                })
              }
            />
            <SectionColorContextMenuSub
              currentColor={section.color ?? null}
              brandColors={brandColors}
              onColorSection={(color) =>
                mutationFns.onEditSection(
                  pattern.workoutId,
                  section.workoutSectionId,
                  {
                    color,
                  },
                )
              }
            />
            <ContextMenuItem
              onSelect={() =>
                mutationFns.onRemoveSection(
                  pattern.workoutId,
                  section.workoutSectionId,
                )
              }
            >
              <ContextMenuItemIcon IconPath={MdRemove} />
              {t('removeSection')}
            </ContextMenuItem>

            <ContextMenuSeparator />

            {commonContextMenuGroup}
          </>
        );
      }
    }
  } else if (pattern.type === 'oneWorkout') {
    switch (pattern.entityType) {
      case 'Set': {
        return (
          <>
            <ContextMenuItem
              onSelect={() => {
                mutationFns.onRemoveSets(
                  pattern.workoutId,
                  pattern.sets.map((d) => d.set.workoutSetId),
                );
              }}
            >
              <ContextMenuItemIcon IconPath={MdRemove} />
              {t('removeSet', { count: pattern.sets.length })}
            </ContextMenuItem>

            {groupBy !== 'all' && (
              <DisabledWithTooltip
                disabled={!canCreateScheme}
                reason={t('permissionDisabled.create', {
                  item: t('schemes'),
                })}
              >
                <SaveAsSchemeContextMenuItem
                  disabled={!canCreateScheme}
                  onSave={() =>
                    setNewSchemeData(
                      pattern.sets.map((data) => ({
                        workoutId: pattern.workoutId,
                        data: data,
                      })),
                    )
                  }
                />
              </DisabledWithTooltip>
            )}

            <ContextMenuSeparator />

            {commonContextMenuGroup}
          </>
        );
      }
      case 'ExerciseMetric': {
        const currentExerciseMetricCount =
          pattern.exerciseMetrics[0]?.exercise.metrics.length ?? 0;
        return (
          <>
            <ContextMenuItem
              onSelect={() => {
                mutationFns.onRemoveExerciseMetrics(
                  pattern.workoutId,
                  pattern.exerciseMetrics.map(
                    (d) => d.metric.workoutExerciseMetricId,
                  ),
                );
              }}
              disabled={
                pattern.exerciseMetrics.length >= currentExerciseMetricCount
              }
            >
              <ContextMenuItemIcon IconPath={MdRemove} />
              {t('removeMetrics')}
            </ContextMenuItem>

            <ContextMenuSeparator />

            {commonContextMenuGroup}
          </>
        );
      }
      case 'Section': {
        return (
          <>
            {groupBy !== 'all' && (
              <>
                <DisabledWithTooltip
                  disabled={!canCreateTemplate}
                  reason={t('permissionDisabled.create', {
                    item: t('templates'),
                  })}
                >
                  <SaveAsTemplateContextMenuItem
                    disabled={!canCreateTemplate}
                    onSelect={() => {
                      // select all sectionIds from the workout to use in the template
                      setWorkoutTemplateData(
                        pattern.workoutId,
                        pattern.sections.map((s) => s.section.workoutSectionId),
                      );
                    }}
                  />
                </DisabledWithTooltip>
                <ContextMenuSeparator />
              </>
            )}
            {commonContextMenuGroup}
          </>
        );
      }
      default:
        return (
          <>
            {groupBy !== 'all' && (
              <>
                <DisabledWithTooltip
                  disabled={!canCreateTemplate}
                  reason={t('permissionDisabled.create', {
                    item: t('templates'),
                  })}
                >
                  <SaveAsTemplateContextMenuItem
                    disabled={!canCreateTemplate}
                    onSelect={() => {
                      // select all sectionIds from the workout to use in the template
                      setWorkoutTemplateData(pattern.workoutId, 'all');
                    }}
                  />
                </DisabledWithTooltip>
                <ContextMenuSeparator />
              </>
            )}
            {commonContextMenuGroup}
          </>
        );
    }
  } else if (pattern.type === 'manySets') {
    return (
      <>
        {groupBy !== 'all' && (
          <DisabledWithTooltip
            disabled={!canCreateScheme}
            reason={t('permissionDisabled.create', { item: t('schemes') })}
          >
            <SaveAsSchemeContextMenuItem
              disabled={!canCreateScheme}
              onSave={() => setNewSchemeData(pattern.sets)}
            />
          </DisabledWithTooltip>
        )}
        {groupBy !== 'all' && <ContextMenuSeparator />}

        {commonContextMenuGroup}
      </>
    );
  } else if (pattern.type === 'manyWorkoutSections') {
    // one entry per workout, sectionIds highlighted within it
    const sectionIdsByWorkout = new Map<string, string[]>();
    pattern.sections.forEach(({ workoutId, data }) => {
      const ids = sectionIdsByWorkout.get(workoutId) ?? [];
      ids.push(data.section.workoutSectionId);
      sectionIdsByWorkout.set(workoutId, ids);
    });
    return (
      <>
        {setMultiWorkoutTemplateData && (
          <ContextMenuItem
            onSelect={() => {
              setMultiWorkoutTemplateData(
                [...sectionIdsByWorkout.entries()].map(
                  ([workoutId, sectionIds]) => ({
                    workoutId,
                    sectionIds,
                  }),
                ),
              );
            }}
          >
            <ContextMenuItemIcon IconPath={MdSaveAs} />
            {t('saveAsTemplate')}
          </ContextMenuItem>
        )}

        {commonContextMenuGroup}
      </>
    );
  } else if (pattern.type === 'manyWorkouts') {
    return (
      <>
        <ContextMenuItem
          onSelect={() => {
            pattern.workouts.forEach(({ workoutId }) => {
              mutationFns.onClearWorkout(workoutId);
            });
          }}
        >
          <ContextMenuItemIcon IconPath={MdRemove} />
          {t('clearWorkout', { count: pattern.workouts.length })}
        </ContextMenuItem>
        {setMultiWorkoutTemplateData && (
          <ContextMenuItem
            onSelect={() => {
              setMultiWorkoutTemplateData(
                pattern.workouts.map(({ workoutId }) => workoutId),
              );
            }}
          >
            <ContextMenuItemIcon IconPath={MdSaveAs} />
            {t('saveAsTemplate')}
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />

        {commonContextMenuGroup}
      </>
    );
  } else {
    return commonContextMenuGroup;
  }
}

/**
 * The "Save as template" action for a selection that contains disabled
 * (read-only) items. `single` saves one workout template for a workout
 * (optionally restricted to sections); `multi` saves a multi-day template.
 */
export type SaveAsTemplateAction =
  | { kind: 'single'; workoutId: string; sectionIds: WorkoutTemplateSelection }
  | {
      kind: 'multi';
      onSelect: () => void;
    };

/**
 * Resolves the "Save as template" action for a selection that contains
 * disabled (read-only) items — e.g. completed workouts, which keep their
 * planned draft layers and can still be turned into templates. Returns null
 * when the pattern has no template action. Mirrors the template actions
 * offered for enabled selections; destructive actions are not offered here.
 */
export function getSaveAsTemplateActionForDisabledSelection(
  pattern: ContextMenuPattern,
  groupBy: BlockGroupBy | undefined,
  setMultiWorkoutTemplateData?: (
    selection: string[] | { workoutId: string; sectionIds: string[] }[],
  ) => void,
): SaveAsTemplateAction | null {
  if (pattern.type === 'oneEntity') {
    const data = pattern.data;
    switch (data.type) {
      case 'Workout':
        return {
          kind: 'single',
          workoutId: pattern.workoutId,
          sectionIds: 'all',
        };
      case 'WorkoutName':
        return groupBy === 'all'
          ? { kind: 'single', workoutId: pattern.workoutId, sectionIds: 'all' }
          : null;
      case 'Section':
        return groupBy !== 'all'
          ? {
              kind: 'single',
              workoutId: pattern.workoutId,
              sectionIds: [data.section.workoutSectionId],
            }
          : null;
      default:
        return null;
    }
  }
  if (pattern.type === 'oneWorkout') {
    if (groupBy === 'all') {
      return null;
    }
    switch (pattern.entityType) {
      case 'Set':
        return null;
      case 'Section':
        return {
          kind: 'single',
          workoutId: pattern.workoutId,
          sectionIds: pattern.sections.map((s) => s.section.workoutSectionId),
        };
      default:
        return {
          kind: 'single',
          workoutId: pattern.workoutId,
          sectionIds: 'all',
        };
    }
  }
  if (pattern.type === 'manyWorkoutSections' && setMultiWorkoutTemplateData) {
    const sections = pattern.sections;
    return {
      kind: 'multi',
      onSelect: () => {
        const sectionIdsByWorkout = new Map<string, string[]>();
        sections.forEach(({ workoutId, data }) => {
          const ids = sectionIdsByWorkout.get(workoutId) ?? [];
          ids.push(data.section.workoutSectionId);
          sectionIdsByWorkout.set(workoutId, ids);
        });
        setMultiWorkoutTemplateData(
          [...sectionIdsByWorkout.entries()].map(([workoutId, sectionIds]) => ({
            workoutId,
            sectionIds,
          })),
        );
      },
    };
  }
  if (pattern.type === 'manyWorkouts' && setMultiWorkoutTemplateData) {
    const workoutIds = pattern.workouts.map(({ workoutId }) => workoutId);
    return {
      kind: 'multi',
      onSelect: () => setMultiWorkoutTemplateData(workoutIds),
    };
  }
  return null;
}

function CommonContextMenuGroup({
  hasDisabledItem,
  hasBulkClear,
  modelName,
  groupedData,
  workoutLayers,
  workouts,
  bulkRunCommands,
  blockWorkoutLayers,
  groupBy,
}: {
  hasDisabledItem: boolean;
  hasBulkClear: boolean;
  modelName: WorkoutLayerModelName;
  groupedData: WorkoutGroupedData[];
  workoutLayers: HasWorkoutLayer[];
  workouts: WorkoutWithCycleAndSession[];
  bulkRunCommands: (commands: WorkoutLayerCommand[]) => void;
  blockWorkoutLayers?: HasWorkoutLayer[];
  groupBy?: BlockGroupBy;
}) {
  const [workoutClipboard, setWorkoutClipboard] = useAtom(workoutClipboardAtom);
  const { setsMetric } = useMetrics();

  const { t } = useTranslation();
  function onBulkClear() {
    const result = commandsForBulkClear(modelName, workoutLayers, groupedData);
    if (result.ok) {
      bulkRunCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  }
  const { toast } = useToast();

  function onCopy() {
    if (groupedData.length === 0) return;
    const days = groupedData.map((wgd) => {
      const workout = workouts.find((w) => w.workoutId === wgd.workoutId);
      return { assignedDate: workout?.assignedDate ?? null, data: wgd };
    });
    try {
      setWorkoutClipboard(buildWorkoutClipboard(days));
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        toast({ title: t('clipboardSizeLimitExceeded'), severity: 'error' });
      } else {
        throw e;
      }
    }
  }

  function onPaste() {
    if (!workoutClipboard) {
      return;
    }

    if (workoutClipboard.days.length > 1) {
      const firstTarget = groupedData[0];
      if (!firstTarget) return;
      const result = collectMultiDayPasteCommandsByPosition(
        workoutClipboard,
        firstTarget.workoutId,
        workouts,
        modelName,
        workoutLayers,
        setsMetric,
        blockWorkoutLayers,
        groupBy,
        firstTarget,
      );
      if (!result.ok) {
        toast({ title: result.error, severity: 'error' });
      } else if (result.value.length > 0) {
        bulkRunCommands(result.value);
      }
      return;
    }

    const clipboardData = workoutClipboard.days[0]?.data;
    if (!clipboardData) {
      return;
    }

    const result = pasteCommands(
      modelName,
      workoutLayers,
      clipboardData,
      groupedData,
      setsMetric,
      blockWorkoutLayers,
    );
    if (result.ok) {
      bulkRunCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  }

  return (
    <ContextMenuGroup>
      <ContextMenuItem onSelect={onCopy} disabled={groupedData.length === 0}>
        <ContextMenuItemIcon IconPath={MdContentCopy} />
        {t('copy')}
      </ContextMenuItem>
      <ContextMenuItem
        disabled={
          !workoutClipboard ||
          workoutClipboard.days.length === 0 ||
          hasDisabledItem
        }
        onSelect={onPaste}
      >
        <ContextMenuItemIcon IconPath={MdOutlineContentPasteGo} />
        {t('paste')}
      </ContextMenuItem>

      {hasBulkClear && (
        <ContextMenuItem onSelect={onBulkClear} disabled={hasDisabledItem}>
          <ContextMenuItemIcon IconPath={MdClose} />
          {t('clear')}
        </ContextMenuItem>
      )}
    </ContextMenuGroup>
  );
}

const AddInstructionContextMenuItem = ({
  onHandleOpenEditInstruction,
}: {
  onHandleOpenEditInstruction: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <ContextMenuItem onSelect={onHandleOpenEditInstruction}>
      <ContextMenuItemIcon IconPath={MdNote} />
      {t('instruction')}
    </ContextMenuItem>
  );
};
const AddSetContextMenuItem = ({
  onAddSet,
  direction,
}: {
  onAddSet: () => void;
  direction?: 'above' | 'below';
}) => {
  const { t } = useTranslation();
  const getLabel = () => {
    if (direction === 'above') {
      return t('addSetAbove');
    } else if (direction === 'below') {
      return t('addSetBelow');
    }
    return t('addSet');
  };

  return (
    <ContextMenuItem onSelect={onAddSet}>
      <ContextMenuItemIcon IconPath={MdPlaylistAdd} />
      {getLabel()}
    </ContextMenuItem>
  );
};

function AddorChangeMetricContextMenuSub({
  metrics,
  currentMetricIds,
  onMetricSelected,
  metricId,
}: {
  metrics: Metric[];
  currentMetricIds: string[];
  onMetricSelected: (metric: Metric) => void;
  metricId?: string;
}) {
  const { t } = useTranslation();
  const { categories } = useMetrics();
  const [search, setSearch] = useState('');
  // Autofocus the search input on keyboard opens only. Focus inside the
  // submenu content keeps it open, so focusing on a pointer hover would pin
  // the submenu open after the mouse leaves; keyboard users need it because
  // Tab is disabled inside Radix menus. The trigger's keydown records the
  // modality; the rAF re-focus wins back focus from Radix's mount effect,
  // which moves focus to the submenu content on keyboard opens (hardcoded in
  // MenuSubContent, not overridable).
  const keyboardOpenRef = useRef(false);
  const focusSearch = useCallback((node: HTMLInputElement | null) => {
    if (!node) {
      keyboardOpenRef.current = false;
      return;
    }
    if (keyboardOpenRef.current) {
      node.focus();
      requestAnimationFrame(() => node.focus());
    }
  }, []);

  const grouped = categories.length > 0;

  const renderItem = (m: Metric, label: ReactNode) => (
    <ContextMenuItem
      key={m.id}
      disabled={currentMetricIds.includes(m.id)}
      onSelect={() => onMetricSelected(m)}
    >
      {label}
    </ContextMenuItem>
  );

  const groups = grouped
    ? groupMetricsByCategory(
        searchMetrics(metrics, categories, search),
        categories,
      )
    : null;

  let content: ReactNode;
  if (!groups) {
    content = metrics.map((m) => renderItem(m, m.name));
  } else {
    content =
      groups.length === 0 ? (
        <ContextMenuItem disabled>{t('noMetricsFound')}</ContextMenuItem>
      ) : (
        groups.flatMap((group) =>
          group.metrics.map((m) =>
            renderItem(
              m,
              group.category ? (
                <>
                  <span className="text-muted-foreground">
                    {group.category.name} &gt;
                  </span>
                  <span className="ml-1">{m.name}</span>
                </>
              ) : (
                m.name
              ),
            ),
          ),
        )
      );
  }

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger
        onKeyDown={(e) => {
          // Radix's SUB_OPEN_KEYS for LTR (the app has no RTL locales). Don't
          // add ArrowLeft speculatively: on a closed submenu it sets the flag
          // without mounting the input, so it never resets and a later
          // pointer open would focus unexpectedly.
          if (['Enter', ' ', 'ArrowRight'].includes(e.key)) {
            keyboardOpenRef.current = true;
          }
        }}
      >
        <ContextMenuItemIcon IconPath={metricId ? MdSwapHoriz : MdLibraryAdd} />
        {metricId ? t('changeMetric') : t('addMetric')}
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        {groups && (
          <div
            className="px-1 pb-1"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                // Tab is unavailable inside Radix menus — route arrows from
                // the input into the metric items instead.
                e.preventDefault();
                const items =
                  e.currentTarget.parentElement?.querySelectorAll<HTMLElement>(
                    '[role="menuitem"]:not([aria-disabled="true"])',
                  );
                const target =
                  e.key === 'ArrowDown'
                    ? items?.[0]
                    : items?.[items.length - 1];
                target?.focus();
                return;
              }
              // Radix menus own keydown for typeahead — stop it so typing
              // reaches the input instead of jumping between items (and so
              // ArrowLeft doesn't close the submenu). Escape must propagate:
              // Radix closes the menu from a document-level listener.
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          >
            <Input
              ref={focusSearch}
              aria-label={t('search')}
              placeholder={t('searchMetrics')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
            {/* Live region announces the empty state as results change; the
                disabled menu item below keeps its menuitem semantics. */}
            <div aria-live="polite" className="sr-only">
              {groups.length === 0 ? t('noMetricsFound') : ''}
            </div>
          </div>
        )}
        {content}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

function BenchmarkMetricContextMenuSub({
  selectedBenchmarkGridId,
  defaultBenchmarkGridId,
  onSelectBenchmarkGrid,
  libraryExerciseId,
}: {
  selectedBenchmarkGridId: string | undefined;
  defaultBenchmarkGridId: string;
  onSelectBenchmarkGrid: (gridId: string) => () => void;
  libraryExerciseId: string;
}) {
  const { t } = useTranslation();
  const { benchmarkGrids } = useBenchmarks();
  // Only show grids on which this exercise is a core exercise; kpi grids are
  // excluded since kpi benchmarks are not supported here yet (PRD req 8).
  const repBenchmarkGrids = getRepBenchmarkGridsForExercise(
    benchmarkGrids,
    libraryExerciseId,
  );

  // Disable the submenu when the exercise is a core exercise on no rep grid.
  const disabled = repBenchmarkGrids.length === 0;

  const selectByDefault = (id: string) => {
    return !selectedBenchmarkGridId && defaultBenchmarkGridId === id;
  };

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger disabled={disabled}>
        <ContextMenuItemIcon IconPath={MdStraighten} />
        {t('selectBenchmarkMetric')}
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        {repBenchmarkGrids.map(({ id, name }) => (
          <ContextMenuItem
            key={id}
            onSelect={onSelectBenchmarkGrid(id)}
            className={
              selectedBenchmarkGridId === id || selectByDefault(id)
                ? 'bg-accent/80'
                : undefined
            }
          >
            {name}
          </ContextMenuItem>
        ))}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

const SaveAsSchemeContextMenuItem = ({
  onSave,
  disabled = false,
  'aria-describedby': describedBy,
}: {
  onSave: () => void;
  disabled?: boolean;
  'aria-describedby'?: string;
}) => {
  const { t } = useTranslation();
  return (
    <ContextMenuItem
      onSelect={onSave}
      disabled={disabled}
      aria-describedby={describedBy}
    >
      <ContextMenuItemIcon IconPath={MdSaveAs} />
      {t('saveAsScheme')}
    </ContextMenuItem>
  );
};

const SaveAsTemplateContextMenuItem = ({
  onSelect,
  disabled = false,
  'aria-describedby': describedBy,
}: {
  onSelect: () => void;
  disabled?: boolean;
  'aria-describedby'?: string;
}) => {
  const { t } = useTranslation();
  return (
    <ContextMenuItem
      onSelect={onSelect}
      disabled={disabled}
      aria-describedby={describedBy}
    >
      <ContextMenuItemIcon IconPath={MdSaveAs} />
      {t('saveAsTemplate')}
    </ContextMenuItem>
  );
};

function canEnableBulkClear(pattern: ContextMenuPattern | null): boolean {
  if (!pattern) {
    return false;
  }

  if (pattern.type === 'manySets') {
    return true;
  }

  if (pattern.type === 'manyWorkouts') {
    return false;
  }

  if (pattern.type === 'oneWorkout') {
    return pattern.entityType === 'Set';
  }

  if (pattern.type === 'oneEntity') {
    return pattern.data.type === 'Set';
  }

  return false;
}
